import React, { useState } from 'react';
import { MonthlyPayrollData, DataSourceType } from '../types/PayrollTypes';
import EditableCell from './EditableCell';

interface MonthlyRowProps {
  month: number;
  label?: string;
  data?: MonthlyPayrollData;
  onUpdate: (data: Partial<MonthlyPayrollData>) => void;
  onReset?: () => void;
  onDelete?: () => void;
  isBonus?: boolean;
}

const MonthlyRow: React.FC<MonthlyRowProps> = ({
  month,
  label,
  data,
  onUpdate,
  onReset,
  onDelete,
  isBonus = false
}) => {
  const [editingCell, setEditingCell] = useState<string | null>(null);

  const getCellStyle = (sourceType?: DataSourceType) => {
    if (!sourceType) return { color: '#6c757d', fontStyle: 'italic' };
    
    switch (sourceType) {
      case DataSourceType.PDF_IMPORTED:
        return { color: '#000000', fontWeight: 'bold' };
      case DataSourceType.ESTIMATED:
        return { color: '#6c757d', fontStyle: 'italic' };
      case DataSourceType.USER_EDITED:
        return { color: '#007bff', fontWeight: 'bold' };
      default:
        return { color: '#6c757d', fontStyle: 'italic' };
    }
  };

  const handleCellEdit = (field: string, value: number) => {
    onUpdate({
      [field]: value,
      sourceType: DataSourceType.USER_EDITED,
      lastUpdated: new Date().toISOString()
    });
    setEditingCell(null);
  };

  const handleEditStart = (field: string) => {
    setEditingCell(field);
  };

  const handleEditCancel = () => {
    setEditingCell(null);
  };

  const fields = [
    { key: 'grossSalary', label: '総支給額' },
    { key: 'healthInsurance', label: '健康保険' },
    { key: 'employmentInsurance', label: '雇用保険' },
    { key: 'nationalPension', label: '厚生年金' },
    { key: 'incomeTax', label: '所得税' },
    { key: 'localTax', label: '住民税' },
    { key: 'definedContribution', label: '確定拠出年金' },
  ];

  const monthLabel = label || (month === 0 ? '賞与' : `${month}月`);

  const handleReset = () => {
    if (onReset) {
      onReset();
    }
  };

  // リセット可能かどうかを判定（PDF_IMPORTEDでなければリセット可能）
  const canReset = data && data.sourceType !== DataSourceType.PDF_IMPORTED;

  return (
    <tr>
      <td style={{ 
        fontWeight: 'bold', 
        padding: '8px 12px',
        backgroundColor: month === 0 ? '#fff3cd' : 'transparent',
        minWidth: '120px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            {monthLabel}
            {data?.sourceType && (
              <div style={{ 
                fontSize: '10px', 
                color: '#6c757d', 
                marginTop: '2px' 
              }}>
                {data.sourceType === DataSourceType.PDF_IMPORTED && 'PDF'}
                {data.sourceType === DataSourceType.ESTIMATED && '推定'}
                {data.sourceType === DataSourceType.USER_EDITED && '編集'}
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '3px' }}>
            {canReset && (
              <button
                onClick={handleReset}
                style={{
                  background: 'transparent',
                  border: '1px solid #dc3545',
                  color: '#dc3545',
                  borderRadius: '3px',
                  padding: '2px 6px',
                  fontSize: '10px',
                  cursor: 'pointer'
                }}
                title="この行をリセット"
              >
                リセット
              </button>
            )}
            {isBonus && onDelete && (
              <button
                onClick={onDelete}
                style={{
                  background: 'transparent',
                  border: '1px solid #dc3545',
                  color: '#dc3545',
                  borderRadius: '3px',
                  padding: '2px 6px',
                  fontSize: '10px',
                  cursor: 'pointer'
                }}
                title="この賞与データを削除"
              >
                削除
              </button>
            )}
          </div>
        </div>
      </td>
      
      {fields.map(field => (
        <EditableCell
          key={field.key}
          value={data?.[field.key as keyof MonthlyPayrollData] as number || 0}
          field={field.key}
          style={getCellStyle(data?.sourceType)}
          onEdit={handleCellEdit}
          isEditing={editingCell === field.key}
          onEditStart={() => handleEditStart(field.key)}
          onEditCancel={handleEditCancel}
        />
      ))}
    </tr>
  );
};

export default MonthlyRow;